// What the puzzles plugin tells the rest of the review: the record of a
// move's puzzle (the `puzzles` API's `statusOf`) and each phase tab's badge.
// Pure, so both are tested without the plugin's store or the event bus.
import { phaseRange, type ReviewState } from '@chess/api-review';
import type { PuzzleStatus } from '@chess/api-puzzles';
import type { Phase } from '@chess/api-model';
import { puzzlesForPhase, type Puzzle } from './finder.ts';
import type { Attempts } from './files.ts';
import type { Solving } from './solving.ts';

/** The phase tabs that carry a count of puzzles. */
export const PHASES: readonly Phase[] = ['opening', 'middlegame', 'ending'];

/**
 * The record for the puzzle of the reader's move at a 1-based ply:
 * `solving` while that puzzle is on the board and waiting for the reader or
 * the engine, else its stored attempt, tactic first.
 */
export function statusOf(ply: number, solving: Solving | undefined, attempts: Attempts['attempts']): PuzzleStatus | undefined {
  if (solving && solving.puzzle.ply === ply - 1 && (solving.status === 'playing' || solving.status === 'thinking')) return 'solving';
  return (attempts[`tactic:${ply - 1}`] ?? attempts[`mistake:${ply - 1}`])?.status;
}

/** Each phase tab's badge: that phase's count, or nothing until a list exists and the game is loaded. */
export function badges(puzzles: Puzzle[] | undefined, state: ReviewState | undefined): Array<{ id: Phase; badge: number | undefined }> {
  const game = state?.game;
  return PHASES.map((phase) => {
    if (!puzzles || !game) return { id: phase, badge: undefined };
    const range = phaseRange(game.phases, game.moves.length, phase, game.sides?.reader);
    return { id: phase, badge: puzzlesForPhase(puzzles, phase, range.fromPly, range.toPly).length };
  });
}
